import { Hash } from "~/interfaces/hashInterfaces";
import { IFindDataStep, IProcessFlowStepConstructorOptions, IProcessFlowVariable } from '~/interfaces/processFlowInterfaces';
import { ConditionalFieldDefinition } from "~/models/conditionalRules/conditionalFieldDefinition";
import ConditionalRuleSet from "~/models/conditionalRules/conditionalRuleSet";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import { ProcessFlowOperator } from "~/models/processFlows/enums/processFlowOperator";
import FindDataStepConditionalRuleSet from "~/models/processFlows/findDataStepConditionalRuleSet";
import ProcessFlowSortationCriteria from "~/models/processFlows/processFlowSortationCriteria";
import ProcessFlowStep from "~/models/processFlows/processFlowStep";
import ProcessFlowStepElementConditionalFields from "~/models/processFlows/processFlowStepElementConditionalFields";
import ProcessFlowVariable from "~/models/processFlows/processFlowVariable";
import ProcessFlowMappedVariable from "~/models/processFlows/steps/processFlowMappedVariable";
import { ProcessFlowFindDataLimitType } from "../../../../formability/processFlowBuilder/enums/processFlowFindDataLimitType";
import { ProcessFlowFindDataWeightType } from "../../../../formability/processFlowBuilder/enums/processFlowFindDataWeightType";
import { ProcessFlowStepType } from '../../../enums/processFlowStepType';

export class ProcessFlowFindDataStep extends ProcessFlowStep implements IFindDataStep {
  step_type: ProcessFlowStepType.FindData;
  random_sort: boolean;
  destination_variable_client_id: string;
  process_operator: ProcessFlowOperator;
  search_criteria_conditional_rule_sets: Hash<FindDataStepConditionalRuleSet> = {};
  search_criteria_mapped_variable_references: Hash<ProcessFlowMappedVariable> = {};
  sortation_criteria: Hash<ProcessFlowSortationCriteria> = {};
  limit_type: ProcessFlowFindDataLimitType;
  limit_property_reference: ProcessFlowMappedVariable;
  limit_numeric_value: number;
  weight_type: ProcessFlowFindDataWeightType;
  weight_property_reference: ProcessFlowMappedVariable;


  constructor(parent, options: IProcessFlowStepConstructorOptions) {
    super(parent, options);
    this.defineProperty('destination_variable_client_id', null);
    this.defineProperty('process_operator', ProcessFlowOperator.SetTo);
    this.defineProperty('random_sort', false);
    this.defineProperty('limit_type', ProcessFlowFindDataLimitType.Number);
    this.defineProperty('limit_numeric_value', null);
    this.defineProperty('weight_type', ProcessFlowFindDataWeightType.Equal);


    if (!(options.json?.search_criteria_conditional_rule_sets?.length > 0)) {
      this.createDefaultRuleSet();
    }
  }

  createDefaultRuleSet(): void {
    if (Object.values(this.search_criteria_conditional_rule_sets).length > 0) return;
    const searchCriteriaRuleSet = ConditionalRuleSetFactory.createForContainer(this, ProcessFlowStepElementConditionalFields.SEARCH_CRITERIA, {}, false, true);
    searchCriteriaRuleSet.conditionalRulesArray.forEach(rule => {
      if ('is_reference' in rule) rule.is_reference = true
    })
    searchCriteriaRuleSet.attachToParent(this);
  }

  get defaultTitle(): string {
    return `Find data for ${this.destinationVariable?.nameOrDefault}`;
  }

  get destinationVariable(): IProcessFlowVariable {
    return ProcessFlowVariable.repo.find(this.destination_variable_client_id);
  }

  // ******************** IConditionalRuleContainer START ******************** //
  createNewRuleSet(fieldName: string, definition: ConditionalFieldDefinition): ConditionalRuleSet {
    if (ProcessFlowStepElementConditionalFields.FIND_DATA_STEP_FIELDS.find(f => f.fieldName === fieldName)) {
      return new FindDataStepConditionalRuleSet(fieldName, definition);
    }
    return super.createNewRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): IConditionalRuleReferencedControl {
    return this.search_criteria_mapped_variable_references[id] || super.getReferencedControl(id);
  }

  // ******************** IConditionalRuleContainer END ******************** //

  protected mappedVariableReferenceCollectionNames(): readonly string[] {
    return super.mappedVariableReferenceCollectionNames(...ProcessFlowStepElementConditionalFields.FIND_DATA_STEP_FIELDS.map(definition => definition.fieldName))
  }

  // ******************** Sortation Criteria START ******************** //
  get sortationCriteriaArray(): ProcessFlowSortationCriteria[] {
    return this.sortProperty('sortation_criteria')
  }

  appendSortCriterion(): void {
    const criterion = new ProcessFlowSortationCriteria(this);
    criterion.sort_order = this.nextSortOrderFor('sortation_criteria');
    criterion.attachToParent(this);
  }

  removeSortCriterion(criterion: ProcessFlowSortationCriteria): void {
    criterion.destroy();
    this.updateSortOrder('sortation_criteria');
  }

  get sortationCriteriaHtmlString(): string {
    if (this.random_sort) {
      return 'Random';
    } else {
      return this.sortationCriteriaArray.length > 0 ? this.sortationCriteriaArray.map(c => c.toHtmlString()).join('; ') : 'None';
    }
  }

  // ******************** Sortation Criteria END ******************** //

  // ******************** Limit START ******************** //
  createLimitPropertyReference(json = {}): ProcessFlowMappedVariable {
    const ref = new ProcessFlowMappedVariable('limit_property_reference', json);
    ref.attachToParent(this);
    return ref;
  }

  get limitPropertyReference(): ProcessFlowMappedVariable {
    return this.limit_property_reference || this.createLimitPropertyReference();
  }

  // ******************** Limit END ******************** //

  // ******************** Weight START ******************** //
  createWeightPropertyReference(json = {}): ProcessFlowMappedVariable {
    const ref = new ProcessFlowMappedVariable('weight_property_reference', json);
    ref.attachToParent(this);
    return ref;
  }

  get weightPropertyReference(): ProcessFlowMappedVariable {
    return this.weight_property_reference || this.createWeightPropertyReference();
  }

  get weightHtmlString(): string {
    if (this.weight_type === ProcessFlowFindDataWeightType.Equal) {
      return 'Equal weight';
    } else {
      return this.weightPropertyReference.source_variable_path?.dynamicFieldName || `<span class="text-danger">Unconfigured</span>`;
    }
  }
  // ******************** Weight END ******************** //

}